<template>
  <qtm-dialog-card
    v-bind="$attrs"
    closable
    :title="title"
    width="800px"
    @update:model-value="onUpdateModelValue"
  >
    <qtm-input-label :label="supplierTitle" required>
      <qtm-select
        v-model="quote"
        :item-title="quoteText"
        :items="quotes"
        return-object
      />
    </qtm-input-label>
    <qtm-input-label class="mb-4 pt-2" label="Supplier Contact" :required="!contact">
      <div class="d-flex justify-space-between">
        <div v-if="contact">
          <span v-text="`${contact.first_name} ${contact.last_name}`" />
          <div v-text="contact.email" />
        </div>
        <change-contact-dialog
          v-model="contact"
          :vendor="quote?.vendor"
        >
          <template v-slot:activator="{ props: activator }">
            <qtm-btn
              v-bind="activator"
              :disabled="!quote"
              :loading="loading"
              prepend-icon="mdi-pencil"
              secondary
            >
              Change Contact
            </qtm-btn>
          </template>
        </change-contact-dialog>
      </div>
    </qtm-input-label>
    <qtm-skeleton v-if="!loaded" />
    <rich-text-editor v-else v-model="localText" :loading="loading" />
    <qtm-input-label v-if="email.attachments.length" class="mt-4" label="Select Attachments">
      <p class="text-caption">
        The selected attachments will be duplicated to the comment.
      </p>
      <order-attachments
        v-model:selected-attachments="attachments"
        allow-select
        class="bg-background"
        :model-value="email.attachments"
        no-upload
        no-delete
        remove-title
      />
    </qtm-input-label>

    <template v-slot:actions>
      <v-spacer />
      <qtm-btn class="mr-2" :loading="loading" size="x-large" tertiary @click="onUpdateModelValue(false)">
        Cancel
      </qtm-btn>
      <qtm-btn :disabled="disabled" :loading="loading" size="x-large" @click="save">
        Save to Chat
      </qtm-btn>
    </template>
  </qtm-dialog-card>
</template>

<script setup lang="ts">
import type { Contact, Quote } from '@quotetome/materials-api'
import ChangeContactDialog from '@/components/vendors/change-contact-dialog.vue'
import OrderAttachments from '@/components/orders/order-attachments.vue'
import RichTextEditor from '@/components/emails/rich-text-editor.vue'

export interface Props {
  email: any
  text?: string
  quotes?: Quote[]
}

const props = withDefaults(defineProps<Props>(), {
  text: '',
  quotes: () => [],
})
const emit = defineEmits(['update:model-value', 'update:text'])

const attachments = ref([])
const contact = ref<Contact>()
const loading = ref(false)
const loaded = ref(false)
const quote = ref()
const localText = ref(props.text)

const disabled = computed(() => !quote.value || !contact.value)
const title = computed(() => {
  const base = 'Add to Chat'

  if (quote.value) {
    return `${base} - ${quoteText(quote.value)}`
  }

  return base
})
let supplierTitle = ''

const quoteText = (q: Quote) => {
  return q.vendor?.name
}

const { $api, $error, $toast } = useNuxtApp()

onMounted(async () => {
  if (props.quotes.length === 1) {
    quote.value = props.quotes[0]
  }
  else {
    quote.value = props.quotes.find(q => q.salesperson?.email === props.email.from_address)
  }
  supplierTitle = quote.value ? 'Supplier' : `Supplier (${props.email.from_address})`

  loading.value = true

  if (!props.text) {
    try {
      localText.value = await $api.v1.notifications.email.text(props.email.id)
    }
    catch (error) {
      $error.report(error)
    }
  }

  loaded.value = true
  loading.value = false
})

const onUpdateModelValue = (modelValue: boolean) => {
  if (!modelValue) {
    emit('update:text', localText.value)
  }
  emit('update:model-value', modelValue)
}

const save = async () => {
  loading.value = true
  try {
    await $api.v1.quotes.chat.create(quote.value.id, {
      attachments_to_copy: attachments.value,
      created_by: contact.value!.id,
      email: props.email.id,
      text: localText.value,
    })

    $toast.success('Saved to chat')
    emit('update:model-value', false)
  }
  catch (error) {
    $error.report(error)
  }
  loading.value = false
}

watch(quote, () => {
  contact.value = quote.value?.salesperson ?? null
})
</script>
